import useUser from '@store/user/user-hook';
import { CaseFileType } from '@api/types/case/case-file/case-file-type';
import { PopoverMenuAction } from '@components/popover-menu/use-popover-menu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faPaperclip,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import CaseFileViewModal, {
  CaseFileViewModalProps,
} from '@pages/app/rca/tabs/components/modals/case-file-manager/case-file-view-modal';
import { isNullOrEmpty } from '@util/string-util';
import { CaseFileListItemResponse } from '@api/types/case/case-file/case-file-list-item.response';
import { useUiPopup } from '@components/ui-popup/ui-popup-provider';
import { useCallback, useMemo } from 'react';
import { useAppSelector } from '@store/store';
import { selectCurrentRcaCaseId } from '@store/rca-editor/selectors';
import { API_URL } from '@api/api';

interface Opt {
  onDelete?: (caseFile: CaseFileListItemResponse) => Promise<boolean>;
}

export default function useCaseFileMenuActions({ onDelete } = {} as Opt) {
  const { showPopup } = useUiPopup();
  const caseId = useAppSelector(selectCurrentRcaCaseId);
  const userCompanyId = useUser()?.companyUserId;

  const getCaseFileMenuActions = useCallback(
    (caseFile: CaseFileListItemResponse) => {
      const { caseFileId, caseFileType, fileLocation, fileMetadataId } =
        caseFile;
      const isLink = caseFileType === CaseFileType.location;
      const items: Array<PopoverMenuAction> = [
        {
          label: 'View Details',
          icon: <FontAwesomeIcon icon={faPaperclip} />,
          onClick: () => {
            showPopup<CaseFileViewModalProps, never>(CaseFileViewModal, {
              caseId: caseId,
              caseFileId: caseFileId,
              onDelete: onDelete ? () => onDelete(caseFile) : undefined,
            });
          },
        },
        {
          label: isLink ? 'Open Location' : 'Download File',
          icon: <FontAwesomeIcon icon={faDownload} />,
          // Download file when clicking this.
          onClick: () => {
            if (!isNullOrEmpty(fileLocation)) {
              window.open(fileLocation, '_blank');
            } else {
              window.open(
                `${API_URL}/api/case/${caseId}/caseFile/file/${fileMetadataId}`,
                '_blank'
              );
            }
          },
        },
      ];

      if (
        caseFile.createdByCompanyUserId === userCompanyId &&
        onDelete != null
      ) {
        items.push({
          label: 'Delete file',
          icon: <FontAwesomeIcon icon={faTrash} />,
          onClick: () => onDelete(caseFile),
        });
      }

      return items;
    },
    [caseId, onDelete, showPopup, userCompanyId]
  );

  return useMemo(
    () => ({
      getCaseFileMenuActions,
    }),
    [getCaseFileMenuActions]
  );
}
